import React, { useState, ChangeEvent } from 'react';
import { Bell, User as UserIcon, LogOut, Settings as SettingsIcon, Phone, Globe } from 'lucide-react';
import ImageCropper from './components/ImageCropper';
import SettingsPanel from './components/SettingsPanel';
import NotificationSettings from './components/NotificationSettings';

import { User } from './models/User';
import { getDisplayName, removeUserProfilePicture, setUserProfilePicture } from './data/userDatabase.ts';
import { useAuth } from './hooks/useAuth';

interface SettingsProps {
  user: User;
  onLogout: () => void;
  onUpdateUser: (updatedUser: User) => void;
  onNavigate?: (page: string) => void;
}

const Settings: React.FC<SettingsProps> = ({ user: propUser, onLogout, onUpdateUser, onNavigate }) => {
  // Local user state for optimistic updates
  const [localUser, setLocalUser] = useState<User>(propUser);
  const [activeTab, setActiveTab] = useState<'profile' | 'app' | 'notifications'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: localUser.fullName || '',
    email: localUser.email || '',
    phoneNumber: localUser.phoneNumber || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showImageCropper, setShowImageCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Update local user when prop user changes
  React.useEffect(() => {
    setLocalUser(propUser);
    setFormData({
      fullName: propUser.fullName || '',
      email: propUser.email || '',
      phoneNumber: propUser.phoneNumber || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }, [propUser]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSaveProfile = () => {
    const updatedUser = {
      ...localUser,
      fullName: formData.fullName,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
    };
    setLocalUser(updatedUser);
    onUpdateUser(updatedUser);
            // Profile updated successfully
  };

  const handleChangePassword = () => {
    if (formData.newPassword !== formData.confirmPassword) {
              // New password and confirm password do not match
      return;
    }
            // Password changed successfully
    setFormData({
      ...formData,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) {
          setSelectedImage(reader.result as string);
          setShowImageCropper(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedImage: string) => {
    try {
      // Optimistically update local state first
      const updatedUser = {
        ...localUser,
        profilePictureUrl: croppedImage,
        hasProfilePicture: true
      };
      setLocalUser(updatedUser);
      
      // Call the specific database function to save the profile picture
      await setUserProfilePicture(localUser.id, croppedImage);
      
      // Update the parent with the updated user object
      onUpdateUser(updatedUser);
      
      setShowImageCropper(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Failed to save profile picture:', error);
      // Revert optimistic update on error
      setLocalUser(localUser);
      alert('Failed to save profile picture. Please try again.');
      setShowImageCropper(false);
      setSelectedImage(null);
    }
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setSelectedImage(null);
  };

  const handleDeleteProfilePicture = async () => {
    if (window.confirm('Are you sure you want to delete your profile picture?')) {
      try {
        // Optimistically update local state first (use undefined to match TypeScript interface)
        const updatedUser = {
          ...localUser,
          profilePictureUrl: undefined,
          hasProfilePicture: false
        };
        setLocalUser(updatedUser);
        
        // Call the actual database function to delete
        await removeUserProfilePicture(localUser.id);
        
        // Update the parent with the corrected user object
        onUpdateUser(updatedUser);
      } catch (error) {
        console.error('Failed to delete profile picture:', error);
        // Revert optimistic update on error
        setLocalUser(localUser);
        alert('Failed to delete profile picture. Please try again.');
      }
    }
  };

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  // Use localUser instead of propUser for display
  const user = localUser;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => {
                  setActiveTab('profile');
                  setIsEditing(false);
                }}
                className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserIcon className="w-4 h-4 inline mr-2" />
                <span className="hidden sm:inline">Profile Settings</span>
                <span className="sm:hidden">Profile</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('app');
                  setIsEditing(false);
                }}
                className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                  activeTab === 'app'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Globe className="w-4 h-4 inline mr-2" />
                <span className="hidden sm:inline">Application Settings</span>
                <span className="sm:hidden">App</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('notifications');
                  setIsEditing(false);
                }}
                className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 touch-manipulation transition-all duration-200 ${
                  activeTab === 'notifications'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Bell className="w-4 h-4 inline mr-2" />
                <span className="hidden sm:inline">Notifications</span>
                <span className="sm:hidden">Notify</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' ? (
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Personal Information</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Your personal details and account information</p>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
              
              {isEditing ? (
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }} className="space-y-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                        Full name
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        id="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email address
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        id="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            fullName: user.fullName || '',
                            email: user.email || '',
                            phoneNumber: user.phoneNumber || '',
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: '',
                          });
                        }}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="border-t border-gray-200">
                  <dl>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Full name</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{getDisplayName(user)}</dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">User ID</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.id}</dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Email address</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.email || 'Not provided'}</dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Phone number</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.phoneNumber || 'Not provided'}</dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>

            {/* Profile Picture */}
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Profile Picture</h2>
              <div className="flex items-center space-x-6 mb-4">
                <div className="relative">
                  <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                    {user.profilePictureUrl ? (
                      <img 
                        key={user.profilePictureUrl}
                        src={user.profilePictureUrl} 
                        alt="Profile" 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-4xl">{getDisplayName(user)?.charAt(0) || 'U'}</span>
                    )}
                  </div>
                </div>
                <div>
                  {user.profilePictureUrl ? (
                    <div className="space-y-2">
                      <div className="flex space-x-3">
                        <label 
                          htmlFor="profile-picture-upload" 
                          className="cursor-pointer px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Upload New Picture
                        </label>
                        <button
                          type="button"
                          onClick={handleDeleteProfilePicture}
                          className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete Current Picture
                        </button>
                      </div>
                      <input 
                        id="profile-picture-upload" 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleImageSelect}
                      />
                      <p className="text-sm text-gray-500">JPG, PNG or GIF. Max size 1MB.</p>
                    </div>
                  ) : (
                    <div>
                      <label 
                        htmlFor="profile-picture-upload" 
                        className="cursor-pointer px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Upload Profile Picture
                      </label>
                      <input 
                        id="profile-picture-upload" 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={handleImageSelect}
                      />
                      <p className="mt-1 text-sm text-gray-500">JPG, PNG or GIF. Max size 1MB.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Change Password</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleChangePassword}
                  className="mt-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        ) : activeTab === 'notifications' ? (
          <div className="bg-gray-50 py-12">
            <NotificationSettings />
          </div>
        ) : (
          <div className="bg-gray-50 py-12">
            <SettingsPanel onSettingsChange={() => {}} />
          </div>
        )}

        {showImageCropper && selectedImage && (
          <ImageCropper
            imageSrc={selectedImage}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        )}
      </main>
    </div>
  );
};

export default Settings; 